import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const JURISDICTIONS = [
    { code: "AE", label: "United Arab Emirates" },
    { code: "SG", label: "Singapore" },
    { code: "GB", label: "United Kingdom" },
    { code: "IN", label: "India" },
    { code: "US", label: "United States" },
    { code: "CH", label: "Switzerland" },
    { code: "HK", label: "Hong Kong" },
    { code: "LU", label: "Luxembourg" },
    { code: "OTHER", label: "Other" },
];

export default function DfiApply() {
    const [searchParams] = useSearchParams();
    const preselected = (searchParams.get("product") || "").toLowerCase();
    const [products, setProducts] = useState([]);
    const [form, setForm] = useState({
        name: "",
        email: "",
        jurisdiction: "AE",
        product: ["arjuna", "veda", "kubera"].includes(preselected) ? preselected : "veda",
        principal_usdt: 50000,
        introducer_id: "",
        notes: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        document.title = "DFI · Apply to Invest";
        fetch(`${API}/api/dfi/products`)
            .then((r) => r.json())
            .then((d) =>
                setProducts(
                    (d.products || []).filter(
                        (p) => p.status === "LIVE" || p.status === "LIVE_PAPER"
                    )
                )
            )
            .catch(() => setProducts([]));
    }, []);

    const update = (k) => (e) =>
        setForm({ ...form, [k]: e.target ? e.target.value : e });

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const body = {
                name: form.name.trim(),
                email: form.email.trim(),
                jurisdiction: form.jurisdiction,
                product: form.product,
                principal_usdt: parseFloat(form.principal_usdt),
                introducer_id: form.introducer_id.trim() || null,
                notes: form.notes.trim() || null,
            };
            const r = await fetch(`${API}/api/dfi/client`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const d = await r.json();
            if (!r.ok)
                throw new Error(d.detail || "Submission failed");
            setSuccess(d.client);
        } catch (err) {
            setError(err.message || "Something went wrong");
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <div
                className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-6"
                data-testid="dfi-apply-success"
            >
                <div className="max-w-xl w-full border border-emerald-400/30 bg-emerald-400/5 p-10">
                    <CheckCircle2 className="h-10 w-10 text-emerald-400 mb-5" />
                    <h1 className="text-3xl font-light tracking-tight">
                        Subscription received, {success.name.split(" ")[0]}.
                    </h1>
                    <p className="mt-4 text-sm text-neutral-400 leading-relaxed">
                        Your DFI subscription is logged under{" "}
                        <span className="font-mono text-amber-300/90">
                            {success.client_id}
                        </span>
                        . Our team will provision your segregated Bybit
                        sub-account and complete KYC within the next 48
                        hours. You'll receive wire instructions once ready.
                    </p>
                    <dl className="mt-8 space-y-3 text-xs border-t border-emerald-400/20 pt-6">
                        <div className="flex justify-between">
                            <dt className="text-neutral-500 uppercase tracking-wider">
                                Product
                            </dt>
                            <dd className="text-neutral-200">
                                {success.product.toUpperCase()}
                            </dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-neutral-500 uppercase tracking-wider">
                                Principal
                            </dt>
                            <dd className="text-neutral-200">
                                {success.principal_usdt.toLocaleString()} USDT
                            </dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-neutral-500 uppercase tracking-wider">
                                High-water mark
                            </dt>
                            <dd className="text-neutral-200">
                                {success.hwm_usdt.toLocaleString()} USDT
                            </dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-neutral-500 uppercase tracking-wider">
                                KYC status
                            </dt>
                            <dd className="text-amber-300 uppercase">
                                {success.kyc_status}
                            </dd>
                        </div>
                    </dl>
                    <Link
                        to="/dfi"
                        className="mt-10 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-amber-300 hover:text-amber-200"
                        data-testid="dfi-apply-back-link"
                    >
                        <ArrowLeft className="h-3 w-3" /> Back to DFI
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen bg-neutral-950 text-neutral-100"
            data-testid="dfi-apply-page"
        >
            <div className="border-b border-neutral-800">
                <div className="max-w-3xl mx-auto px-6 py-14">
                    <Link
                        to="/dfi"
                        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-neutral-500 hover:text-neutral-300 mb-8"
                        data-testid="dfi-apply-nav-back"
                    >
                        <ArrowLeft className="h-3 w-3" /> DFI
                    </Link>
                    <div className="text-xs tracking-[0.3em] text-amber-300/80 uppercase mb-5">
                        Subscription application
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-light tracking-tight max-w-2xl">
                        Open a segregated sub-account.
                    </h1>
                    <p className="mt-4 text-sm text-neutral-400 leading-relaxed max-w-xl">
                        50,000 USDT minimum · 20% performance fee ·
                        high-water-mark protected · 30-day soft lock.
                    </p>
                </div>
            </div>
            <div className="max-w-3xl mx-auto px-6 py-14">
                <form onSubmit={submit} className="space-y-7" data-testid="dfi-apply-form">
                    <Field label="Full legal name" required>
                        <input
                            type="text"
                            required
                            value={form.name}
                            onChange={update("name")}
                            data-testid="dfi-apply-name"
                            className="w-full bg-neutral-900 border border-neutral-800 px-4 py-3 text-sm focus:border-amber-300/50 focus:outline-none"
                            placeholder="As on passport"
                        />
                    </Field>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                        <Field label="Email" required>
                            <input
                                type="email"
                                required
                                value={form.email}
                                onChange={update("email")}
                                data-testid="dfi-apply-email"
                                className="w-full bg-neutral-900 border border-neutral-800 px-4 py-3 text-sm focus:border-amber-300/50 focus:outline-none"
                                placeholder="you@domain.com"
                            />
                        </Field>
                        <Field label="Tax residency" required>
                            <select
                                required
                                value={form.jurisdiction}
                                onChange={update("jurisdiction")}
                                data-testid="dfi-apply-jurisdiction"
                                className="w-full bg-neutral-900 border border-neutral-800 px-4 py-3 text-sm focus:border-amber-300/50 focus:outline-none"
                            >
                                {JURISDICTIONS.map((j) => (
                                    <option key={j.code} value={j.code}>
                                        {j.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                        <Field label="Product" required>
                            <select
                                required
                                value={form.product}
                                onChange={update("product")}
                                data-testid="dfi-apply-product"
                                className="w-full bg-neutral-900 border border-neutral-800 px-4 py-3 text-sm focus:border-amber-300/50 focus:outline-none"
                            >
                                {products.map((p) => (
                                    <option
                                        key={p.name.toLowerCase()}
                                        value={p.name.toLowerCase()}
                                    >
                                        {p.name} · {p.tagline}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Principal (USDT)" required>
                            <input
                                type="number"
                                required
                                min="50000"
                                step="1000"
                                value={form.principal_usdt}
                                onChange={update("principal_usdt")}
                                data-testid="dfi-apply-principal"
                                className="w-full bg-neutral-900 border border-neutral-800 px-4 py-3 text-sm focus:border-amber-300/50 focus:outline-none"
                            />
                        </Field>
                    </div>
                    <Field label="Introducer / referral code (optional)">
                        <input
                            type="text"
                            value={form.introducer_id}
                            onChange={update("introducer_id")}
                            data-testid="dfi-apply-introducer"
                            className="w-full bg-neutral-900 border border-neutral-800 px-4 py-3 text-sm focus:border-amber-300/50 focus:outline-none"
                            placeholder="If any"
                        />
                    </Field>
                    <Field label="Notes (optional)">
                        <textarea
                            rows={3}
                            value={form.notes}
                            onChange={update("notes")}
                            data-testid="dfi-apply-notes"
                            className="w-full bg-neutral-900 border border-neutral-800 px-4 py-3 text-sm focus:border-amber-300/50 focus:outline-none"
                            placeholder="Mandate details, lockup preference, timing"
                        />
                    </Field>

                    {error && (
                        <div
                            className="border border-red-400/30 bg-red-400/5 text-red-300 text-sm px-4 py-3"
                            data-testid="dfi-apply-error"
                        >
                            {error}
                        </div>
                    )}

                    <div className="pt-5 border-t border-neutral-800 flex items-center justify-between gap-4">
                        <p className="text-xs text-neutral-500 max-w-md leading-relaxed">
                            Submitting creates a subscription draft. KYC and
                            sub-account provisioning follow within 48 hours.
                        </p>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="dfi-apply-submit"
                            className="inline-flex items-center gap-2 bg-amber-300 text-neutral-950 px-7 py-3 text-xs tracking-[0.2em] uppercase font-medium hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting && (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            )}
                            {submitting ? "Submitting" : "Submit application"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-2 inline-block">
                {label}
                {required && <span className="text-amber-300/80"> *</span>}
            </span>
            {children}
        </label>
    );
}
